import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn.js';

/// Painel lateral à direita: para consultar ou editar algo ao lado da tela em uso sem sair dela,
/// como abrir o Prontuário a partir da Agenda ou atualizar a ficha durante o atendimento.
///
/// A gaveta (`Drawer`) é a navegação do celular, estreita e à esquerda; o `Modal` cobre a tela no
/// centro. Aqui o conteúdo é largo, a página de trás continua à vista no computador, e no celular o
/// painel ocupa a largura toda. É diálogo de verdade (Radix): prende o foco, fecha no Esc e devolve
/// o foco a quem o abriu.
export function SidePanel({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  /// As ações ficam paradas no rodapé enquanto o conteúdo rola.
  footer?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px] data-[state=open]:animate-[fade-in_.14s_ease-out]" />
        <Dialog.Content
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full max-w-[36rem] flex-col border-l border-surface-border bg-surface-card shadow-[0_24px_80px_rgb(0_0_0/.55)] outline-none data-[state=open]:animate-[side-panel-in_.18s_ease-out]',
            className,
          )}
        >
          <div className="flex shrink-0 items-start gap-4 border-b border-surface-border px-4 py-4 sm:px-6">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-lg font-semibold text-content">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm leading-6 text-content-muted">
                  {description}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="grid size-10 shrink-0 place-items-center rounded-xl border border-surface-border text-content-muted transition hover:border-brand-500/60 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
            {children}
          </div>
          {footer ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-surface-border px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:px-6">
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
